import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import { getAvailableProductOptionTypes } from '@helpers/filters'
import getCollectionProductData from '@helpers/filters/getCollectionProductData'
import styles from './styles.module.scss'
import { Collection } from 'types/collection'
import { FilterOption } from 'types/filters'

const CollectionSectionHeader = dynamic(
  () => import('../CollectionSectionHeader')
)
const CollectionProductList = dynamic(() => import('../CollectionProductList'))
const CollectionNoResult = dynamic(() => import('../CollectionNoResult'))

type RenderCollectionProps = {
  collection: Collection
  title: string
  desc: string
  collectionIndex: number
  header?: string
  showProductType: boolean
}

const RenderCollection = ({
  collection,
  title = '',
  desc = '',
  collectionIndex,
  header,
  showProductType
}: RenderCollectionProps) => {
  const hideTitle =
    !!title &&
    collectionIndex === 0 &&
    title.toLowerCase() === header?.toLowerCase()

  return (
    <section className={styles.collection}>
      <CollectionSectionHeader
        title={title}
        desc={desc}
        hideTitle={hideTitle}
      />
      {collection.items.length && (
        <CollectionProductList
          title={title}
          products={collection.items}
          showProductType={showProductType}
        />
      )}
    </section>
  )
}

type CollectionWrapperProps = {
  collections: Collection[]
  header?: string
  filterOptions: FilterOption[]
  variantExist: boolean
  showProductType: boolean
}

const CollectionWrapper = ({
  collections,
  header,
  filterOptions,
  variantExist,
  showProductType
}: CollectionWrapperProps) => {
  const { collectionsWithFilteredProducts } = useMemo(
    () =>
      getCollectionProductData({
        collections,
        filterOptions,
        variantExist
      }),
    [collections, filterOptions, variantExist]
  )
  const numberOfCollections = collectionsWithFilteredProducts.length

  if (numberOfCollections === 0) {
    const availableFilters = getAvailableProductOptionTypes(collections)
    const selectedOptions: FilterOption[] = filterOptions
      .filter(
        f => availableFilters.findIndex(av => av.toLowerCase() === f.type) > -1
      )
      .sort((a, b) => (b.position < a.position ? 1 : -1))

    const { title, description } = collections[0] || {}

    return (
      <section className={styles.collection}>
        {header !== null && (
          <CollectionSectionHeader
            title={title}
            desc={description}
            hideTitle={false}
          />
        )}
        <CollectionNoResult selectedOptions={selectedOptions} />
      </section>
    )
  }

  return collectionsWithFilteredProducts.map((collection, i) => (
    <RenderCollection
      key={collection.product_type}
      collection={collection}
      title={collection.title}
      desc={collection.description}
      collectionIndex={i}
      header={header}
      showProductType={showProductType}
    />
  ))
}

export default CollectionWrapper
